import type { Estimate } from '@/types/estimate';
import { documentAddressLines, type DocumentAddressInput } from './document-address';

/**
 * Estimates read addresses the same way every other customer document does.
 * This stays as the name the estimate documents already import, but there is
 * ONE implementation now: two copies is how the invoice side drifted into
 * calling `.trim()` on an object.
 */
export function estimateAddressLines(address?: DocumentAddressInput): string[] {
  return documentAddressLines(address);
}

/**
 * A bill-to snapshot with a `billToId` is a DIFFERENT party than the job
 * customer (a catalog entry pointing at another contact). Self / additional
 * person billing has no id and stays the customer's own details.
 */
export function estimateHasSeparateBillToParty(estimate: Estimate): boolean {
  // The persisted selector and the snapshot's own id are written together, but
  // an older snapshot may lack the id — either one marks a separate party.
  return Boolean(estimate.billToId || estimate.billToContact?.billToId);
}

/**
 * Billing belongs under Billing Details and must never fall back to the work
 * site. The legacy single-line `customer.address` is the CUSTOMER's billing
 * address, so it may only stand in when the customer is the one being billed:
 * a separate bill-to party without an address on file prints no address rather
 * than the customer's under the payer's name.
 */
export function estimateBillingAddressLines(estimate: Estimate): string[] {
  const billingLines = estimateAddressLines(estimate.billingAddress);
  if (billingLines.length > 0) return billingLines;
  if (estimateHasSeparateBillToParty(estimate)) return [];
  return estimate.customer?.address?.trim() ? [estimate.customer.address.trim()] : [];
}

export interface EstimateBillToParty {
  name: string;
  email: string | null;
  phone: string | null;
  addressLines: string[];
}

/**
 * The party a customer document prints under "Bill To". The bill-to CONTACT
 * wins over the job customer; only when the customer is the payer do its
 * email / phone fill the gaps. A separate party shows ITS OWN details or
 * nothing — the estimate snapshot carries no phone for it, so none prints —
 * mirroring the invoice documents.
 */
export function estimateBillToParty(estimate: Estimate): EstimateBillToParty {
  // `customer` is typed non-null, but a frozen snapshot / malformed payload
  // could still arrive without it — deref defensively so the document never
  // crashes.
  const customer = estimate.customer ?? null;
  const contact = estimate.billToContact ?? null;
  const separate = estimateHasSeparateBillToParty(estimate);
  return {
    name: contact?.contactName ?? contact?.name ?? contact?.label ?? customer?.name ?? '—',
    email: contact?.email ?? (separate ? null : (customer?.email ?? null)),
    phone: separate ? null : (customer?.phone ?? null),
    addressLines: estimateBillingAddressLines(estimate),
  };
}

/** Compact service-site value for the metadata column on customer documents. */
export function estimateServiceAddressText(estimate: Estimate): string {
  return estimateAddressLines(estimate.serviceAddress).join(', ');
}

/**
 * Address-only titles were generated briefly when the default
 * `{job_type} — {short_address}` template had no job type. Once the service
 * address has its own labelled row, repeating that street as "Title" is both
 * redundant and misleading. Preserve every real/custom title.
 */
export function estimateCustomerVisibleTitle(estimate: Estimate): string | null {
  const title = estimate.title?.trim();
  if (!title) return null;

  if (!estimate.jobTypeName?.trim()) {
    const serviceLines = estimateAddressLines(estimate.serviceAddress);
    const addressOnlyTitles = [estimate.serviceAddress?.street?.trim(), serviceLines.join(', ')]
      .filter((value): value is string => Boolean(value))
      .map((value) => value.toLowerCase());
    if (addressOnlyTitles.includes(title.toLowerCase())) return null;
  }

  return title;
}
